/**
 * fbs#99 (Chase 10-07): position and zoom the practice logo before it is saved, like the
 * Provider Profile photo. The frame is the stored size, 600 × 200 (3:1, how the logo sits at
 * the top of a form). Unlike a photo, a logo starts fully visible ("contain", zoom 1) and
 * zooms in from there, and a logo smaller than the frame can be placed anywhere inside it
 * (it starts left-aligned and centred vertically).
 *
 * Pure: (x, y) is the image's centre in frame pixels.
 */
export const LOGO_W = 600;
export const LOGO_H = 200;
export const LOGO_MAX_ZOOM = 4;

export interface LogoCrop {
  w: number; // source size
  h: number;
  zoom: number;
  x: number;
  y: number;
}

/** The scale at which the whole image just fits the frame. */
export function baseScale(w: number, h: number): number {
  return Math.min(LOGO_W / w, LOGO_H / h);
}

/** How far in it may zoom: up to 4×, but not past twice the source's own pixels. */
export function maxZoom(w: number, h: number): number {
  return Math.max(1, Math.min(LOGO_MAX_ZOOM, 2 / baseScale(w, h)));
}

/** Keep the image where it can be seen: a bigger-than-frame side can't leave a gap at the
 *  edge; a smaller one stays fully inside the frame. */
export function clampCrop(c: LogoCrop): LogoCrop {
  const s = baseScale(c.w, c.h) * c.zoom;
  const clamp1 = (v: number, size: number, frame: number) =>
    size >= frame
      ? Math.min(size / 2, Math.max(frame - size / 2, v))
      : Math.min(frame - size / 2, Math.max(size / 2, v));
  return { ...c, x: clamp1(c.x, c.w * s, LOGO_W), y: clamp1(c.y, c.h * s, LOGO_H) };
}

export function initialCrop(w: number, h: number): LogoCrop {
  const s = baseScale(w, h);
  return clampCrop({ w, h, zoom: 1, x: (w * s) / 2, y: LOGO_H / 2 });
}

export function withZoom(c: LogoCrop, zoom: number): LogoCrop {
  const z = Math.min(maxZoom(c.w, c.h), Math.max(1, zoom));
  return clampCrop({ ...c, zoom: z });
}

export function moveBy(c: LogoCrop, dx: number, dy: number): LogoCrop {
  return clampCrop({ ...c, x: c.x + dx, y: c.y + dy });
}

/** Where to draw the source in frame pixels. */
export function drawRect(c: LogoCrop): { dx: number; dy: number; dw: number; dh: number } {
  const s = baseScale(c.w, c.h) * c.zoom;
  const dw = c.w * s;
  const dh = c.h * s;
  return { dx: c.x - dw / 2, dy: c.y - dh / 2, dw, dh };
}

/** The bounding box of the pixels that aren't fully transparent (RGBA data, row-major), or
 *  null when the whole frame is empty. Used to trim the empty margin off a PNG logo. */
export function opaqueBounds(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): { x: number; y: number; w: number; h: number } | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3]! > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}
